import { useGetBenchmarkQuery } from '@vizit/api';
import { AlertLevel, Button, ButtonLevel, ButtonSize, useToast } from '@vizit/atoms';
import { BenchmarkImageSortOption, useBenchmarkCollections, useBenchmarkImageUpload } from '@vizit/collections';
import { useBenchmarkImages } from '@vizit/collections';
import { BENCHMARK_UNCATEGORIZED_IMAGE_EXTENSIONS } from '@vizit/constants';
import { AlertCircleIcon, ClockRewindIcon, SortAlphabeticalIcon, UploadCloud02Icon } from '@vizit/icons';
import { LibraryEmptyState } from '@vizit/molecules';
import { BenchmarkDetailsHeader, BenchmarkImageGrid, BenchmarkUploadDialog, ClearableSelect } from '@vizit/organisms';
import { BenchmarkImagePreviewDialog } from '@vizit/organisms';
import { useUploadQueue } from '@vizit/organisms';
import type { BenchmarkImagePreview, ClearableSelectItem } from '@vizit/organisms';
import { colors } from '@vizit/theme';
import * as React from 'react';

const SORT_ICON_COLOR = colors['gray-utility']['400'];

const SORT_ITEMS: ClearableSelectItem[] = [
  {
    id: BenchmarkImageSortOption.Newest,
    label: 'Newest',
    icon: <ClockRewindIcon size={14} stroke={SORT_ICON_COLOR} aria-hidden />,
  },
  {
    id: BenchmarkImageSortOption.Oldest,
    label: 'Oldest',
    icon: <ClockRewindIcon size={14} stroke={SORT_ICON_COLOR} aria-hidden />,
  },
  {
    id: BenchmarkImageSortOption.Name,
    label: 'Name (A-Z)',
    icon: <SortAlphabeticalIcon size={14} className="text-gray-utility-400" aria-hidden />,
  },
];

interface BenchmarkDetailsPageProps {
  /**
   * The benchmark to show
   */
  benchmarkId: string;
  /**
   * The active workspace
   *
   * @optional
   */
  workspaceId?: string;
  /**
   * How the images are sorted, kept in the URL by the route
   */
  sortOption: BenchmarkImageSortOption;
  /**
   * Called when the sort changes
   */
  onSortOptionChange: (option: BenchmarkImageSortOption) => void;
}

/**
 * A custom benchmark's page: its name, image type and image count, and its images. Product categories and scores
 * against them are added on top of this page later; until then images show without scores.
 */
function BenchmarkDetailsPage(props: BenchmarkDetailsPageProps) {
  const { benchmarkId, workspaceId, sortOption, onSortOptionChange } = props;

  const benchmarkQuery = useGetBenchmarkQuery(
    { workspace_id: workspaceId ?? '', benchmark_id: benchmarkId },
    { enabled: !!workspaceId && !!benchmarkId },
  );
  const benchmark = benchmarkQuery.data;

  const { toUploadQueueItems, uploadingImageIds, failedImageIds } = useBenchmarkImageUpload({
    workspaceId,
    benchmarkId,
  });

  const {
    images,
    items,
    totalCount,
    processingCount,
    isLoading: isImagesLoading,
  } = useBenchmarkImages({
    workspaceId,
    benchmarkId,
    sortOption,
    uploadingImageIds,
    failedImageIds,
  });

  const { collections } = useBenchmarkCollections({ workspaceId, benchmarkId });
  const { onAddFiles } = useUploadQueue();
  const { addToast } = useToast();

  const [isUploadOpen, setIsUploadOpen] = React.useState(false);
  const [previewImageId, setPreviewImageId] = React.useState<string | null>(null);

  const previewImage = images.find((image) => image.id === previewImageId);
  const preview: BenchmarkImagePreview | null = previewImage
    ? {
        name: previewImage.name,
        imageUrl: previewImage.previewUrl,
        fallbackImageUrl: previewImage.originalUrl,
        // The benchmark's score under a product category; null until the benchmark has one.
        score: previewImage.vizitScore,
        // Hero benchmarks hold hero images only; carousel images show their class, never "Carousel".
        assetClass: benchmark?.scope === 'hero' ? 'Hero' : (previewImage.classification ?? 'Unclassified'),
      }
    : null;
  const openUpload = React.useCallback(() => setIsUploadOpen(true), []);

  const handleUpload = (files: File[]) => {
    try {
      onAddFiles(toUploadQueueItems(files));
    } catch {
      addToast({
        title: 'Upload queue is full',
        description: 'The upload queue is full. Please try again later.',
        level: AlertLevel.Error,
      });
    }
  };

  if (benchmarkQuery.isError) {
    return (
      <div className="flex flex-col px-8 pt-12 pb-8 max-sm:px-4 max-sm:pt-8">
        <LibraryEmptyState
          heading="Couldn't load this benchmark"
          description="It may have been deleted, or something went wrong. Try again in a moment."
          icon={AlertCircleIcon}
        >
          <Button level={ButtonLevel.Secondary} size={ButtonSize.Sm} onPress={() => benchmarkQuery.refetch()}>
            Try again
          </Button>
        </LibraryEmptyState>
      </div>
    );
  }

  // The list's total follows uploads as they land; the benchmark's own count only updates when it is refetched.
  const imageCount = isImagesLoading ? (benchmark?.imageCount ?? 0) : totalCount;
  const hasImages = imageCount > 0;

  const uploadButton = (
    <Button
      level={ButtonLevel.Primary}
      size={ButtonSize.Lg}
      iconStart={<UploadCloud02Icon size={18} aria-hidden />}
      onPress={openUpload}
      isDisabled={!benchmark}
    >
      Upload images
    </Button>
  );

  return (
    <div className="flex flex-col gap-10 px-8 pt-12 pb-8 max-sm:px-4 max-sm:pt-8">
      <BenchmarkDetailsHeader
        name={benchmark?.name ?? ''}
        description={benchmark?.description}
        scope={benchmark?.scope ?? 'carousel'}
        imageCount={imageCount}
        minimumImages={benchmark?.minimumImages ?? 0}
        processingCount={processingCount}
        isLoading={!benchmark}
        actions={
          <>
            {hasImages && (
              <ClearableSelect
                ariaLabel="Sort images"
                placeholder="Newest"
                items={SORT_ITEMS}
                selectedKey={sortOption}
                onSelectionChange={(key) => key && onSortOptionChange(key as BenchmarkImageSortOption)}
                includeAllOption={false}
                isClearable={false}
                className="min-w-[180px]"
              />
            )}
            {uploadButton}
          </>
        }
      />

      <BenchmarkImageGrid
        images={items}
        totalCount={totalCount}
        minimumImages={benchmark?.minimumImages ?? 0}
        isLoading={isImagesLoading}
        emptyStateAction={uploadButton}
        onImagePress={setPreviewImageId}
      />

      <BenchmarkImagePreviewDialog image={preview} onOpenChange={(isOpen) => !isOpen && setPreviewImageId(null)} />

      <BenchmarkUploadDialog
        isOpen={isUploadOpen}
        onOpenChange={setIsUploadOpen}
        rescoreCollectionCount={collections?.length}
        // The benchmark has no product category yet, so the API reads each image's format from its file name.
        allowedExtensions={BENCHMARK_UNCATEGORIZED_IMAGE_EXTENSIONS}
        onSubmit={handleUpload}
      />
    </div>
  );
}

export { BenchmarkDetailsPage };
export type { BenchmarkDetailsPageProps };
